import { openDB, DBSchema, IDBPDatabase } from "idb";

const DB_NAME = "FileStorageDB";
const STORE_NAME = "files";
const DB_VERSION = 3;

export interface StoredFile {
  id: string;
  needId: string;
  name: string;
  type: string;
  size: number;
  data: ArrayBuffer;
  documentId?: string;
  uploading?: boolean;
  createdAt: number;
}

interface FileDB extends DBSchema {
  files: {
    key: string;
    value: StoredFile;
    indexes: {
      needId: string;
    };
  };
}

export const shareFiles = async (shareData: ShareData) => {
  if (navigator.canShare?.({ files: shareData.files })) {
    try {
      await navigator.share(shareData);
      console.log("Share was successful.");
    } catch (error) {
      console.error("Sharing failed:", error);
    }
  } else {
    console.log(`Your system doesn't support sharing files.`);
  }
};

function getDB(): Promise<IDBPDatabase<FileDB>> {
  return openDB<FileDB>(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion) {
      if (oldVersion < 3) {
        if (db.objectStoreNames.contains(STORE_NAME)) {
          db.deleteObjectStore(STORE_NAME);
        }
      }

      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
        store.createIndex("needId", "needId", { unique: false });
      }
    },
  });
}

export async function saveFile(needId: string, file: File): Promise<string> {
  const db = await getDB();

  const data = await file.arrayBuffer();

  const id = crypto.randomUUID();

  const storedFile: StoredFile = {
    id,
    needId,
    name: file.name,
    type: file.type,
    size: file.size,
    data,
    createdAt: Date.now(),
  };

  await db.put(STORE_NAME, storedFile);
  return id;
}

export async function getFiles(needId: string): Promise<StoredFile[]> {
  const db = await getDB();
  return db.getAllFromIndex(STORE_NAME, "needId", needId);
}

export async function deleteSingleFile(storedFileId: string): Promise<void> {
  const db = await getDB();
  await db.delete(STORE_NAME, storedFileId);
}

export async function deleteFiles(needId: string): Promise<void> {
  const db = await getDB();
  const files = await getFiles(needId);

  await Promise.all(files.map((f) => db.delete(STORE_NAME, f.id)));
}

export async function deleteAllFiles(): Promise<void> {
  const db = await getDB();
  await db.clear(STORE_NAME);
}

export async function updateStoredFileDocumentId(
  storedFileId: string,
  documentId: string,
): Promise<void> {
  const db = await getDB();
  const match = await db.get(STORE_NAME, storedFileId);

  if (!match) {
    console.warn(`File not found for update: ${storedFileId}`);
    return;
  }

  const updated: StoredFile = { ...match, documentId, uploading: false };

  await db.put(STORE_NAME, updated);
}

export async function markFileUploading(
  storedFileId: string,
  uploading: boolean,
): Promise<void> {
  const db = await getDB();
  const match = await db.get(STORE_NAME, storedFileId);

  if (!match) return;

  const updated: StoredFile = { ...match, uploading };
  await db.put(STORE_NAME, updated);
}
